import { FormEvent, Fragment, useState } from 'react'
import classes from './ChatWidget.module.scss'
import FeatureModal, { ModalFeedback, ModalLoading } from './FeatureModal'
import CargoTrackingCard from './CargoTrackingCard'
import { formatChatDate, getChatErrorMessage } from './chatFormatters'
import { CargoTracking, ChatOrder, ChatRequestHandler } from './types'
import { IconEye } from './icons'
import OrderDetailsPanel from './OrderDetailsPanel'

interface Props {
  onClose: () => void
  onRequest: ChatRequestHandler
}

export default function CargoTrackingModal({ onClose, onRequest }: Props) {
  const [orderNumber, setOrderNumber] = useState('')
  const [orders, setOrders] = useState<ChatOrder[]>([])
  const [tracking, setTracking] = useState<CargoTracking | null>(null)
  const [expandedOrderKey, setExpandedOrderKey] = useState<string | null>(null)
  const [operation, setOperation] = useState<'orders' | 'tracking' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [emptyMessage, setEmptyMessage] = useState<string | null>(null)

  const busy = operation !== null

  const resetFeedback = () => {
    setError(null)
    setEmptyMessage(null)
  }

  const loadRecentOrders = async () => {
    if (busy) return
    resetFeedback()
    setTracking(null)
    setOperation('orders')

    try {
      const response = await onRequest({
        buttonIntent: 'getSiparisler',
        // Faz-44: liste yalnız sipariş seçtirmek için çekilir; kargo notu gösterilmez, bu yüzden
        // backend'in sipariş başına kargo sorgusu kapatılır (her kart bir SAP çağrısı demek).
        buttonParameters: { count: '10', cargo: 'false' },
      })
      const recentOrders = response.orders ?? []
      setOrders(recentOrders)
      setExpandedOrderKey(null)
      if (recentOrders.length === 0) {
        setEmptyMessage(response.text || 'Son siparişlerinizde gösterilecek bir kayıt bulunamadı.')
      }
    } catch (requestError: unknown) {
      setOrders([])
      setError(getChatErrorMessage(requestError, 'Son siparişleriniz şu anda alınamıyor.'))
    } finally {
      setOperation(null)
    }
  }

  const trackOrder = async (rawOrderNumber: string) => {
    const normalizedOrderNumber = rawOrderNumber.trim()
    if (!normalizedOrderNumber) {
      setError('Sipariş numarası zorunludur.')
      return
    }
    if (busy) return

    resetFeedback()
    setTracking(null)
    setOrderNumber(normalizedOrderNumber)
    setOperation('tracking')

    try {
      const response = await onRequest({
        buttonIntent: 'getKargoTakip',
        buttonParameters: { orderNo: normalizedOrderNumber },
      })
      if (response.cargoTracking) {
        setTracking(response.cargoTracking)
      } else {
        setEmptyMessage(response.text || 'Bu sipariş için kargo bilgisi bulunamadı.')
      }
    } catch (requestError: unknown) {
      setError(getChatErrorMessage(requestError, 'Kargo bilgisi şu anda alınamıyor.'))
    } finally {
      setOperation(null)
    }
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    trackOrder(orderNumber)
  }

  return (
    <FeatureModal
      titleId='cargo-tracking-modal-title'
      title='Kargo Takip'
      description='Sipariş numaranızla sorgulayın veya son siparişlerinizden seçim yapın.'
      onClose={onClose}
      busy={busy}
    >
      <form className={classes.modalQueryForm} onSubmit={handleSubmit} noValidate>
        <label className={classes.modalField}>
          <span className={classes.modalFieldLabel}>Sipariş no ile sorgula</span>
          <div className={classes.modalInlineField}>
            <input
              data-modal-initial-focus
              className={classes.modalInput}
              value={orderNumber}
              onChange={event => setOrderNumber(event.target.value)}
              placeholder='Örn. 0002874324'
              autoComplete='off'
              disabled={busy}
            />
            <button type='submit' className={classes.modalPrimaryButton} disabled={busy || !orderNumber.trim()}>
              Sorgula
            </button>
          </div>
        </label>

        <div className={classes.modalDivider}>
          <span>veya</span>
        </div>

        <button type='button' className={classes.modalSecondaryButton} onClick={loadRecentOrders} disabled={busy}>
          Son siparişlerim
        </button>
      </form>

      {operation === 'orders' && <ModalLoading label='Son siparişleriniz yükleniyor…' />}
      {operation === 'tracking' && <ModalLoading label='Kargo bilgisi sorgulanıyor…' />}
      {error && <ModalFeedback tone='error'>{error}</ModalFeedback>}
      {emptyMessage && <ModalFeedback tone='empty'>{emptyMessage}</ModalFeedback>}

      {orders.length > 0 && (
        <section className={classes.modalSection} aria-labelledby='recent-orders-title'>
          <div className={classes.modalSectionHeading}>
            <h3 id='recent-orders-title'>Son siparişlerim</h3>
            <span>{orders.length} kayıt</span>
          </div>
          <div className={classes.modalOrdersTableWrap} tabIndex={0} aria-label='Son siparişler tablosu'>
            <table className={classes.modalOrdersTable}>
              <thead>
                <tr>
                  <th scope='col'>Sipariş no</th>
                  <th scope='col'>Tarih</th>
                  <th scope='col'>Kargo durumu</th>
                  <th scope='col'>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, index) => {
                  const canTrack = Boolean(order.deliveryNumber?.trim())
                  const orderKey = `${order.companyCode || 'sirket'}-${order.orderNumber}-${index}`
                  const detailExpanded = expandedOrderKey === orderKey

                  return (
                    <Fragment key={orderKey}>
                      <tr className={detailExpanded ? classes.modalOrderDataRowExpanded : undefined}>
                        <th scope='row'>{order.orderNumber}</th>
                        <td>{formatChatDate(order.orderDate)}</td>
                        <td>
                          <span className={canTrack ? classes.modalReadyBadge : classes.modalPendingBadge}>
                            {canTrack ? 'Kargoya verildi' : 'Teslimat no bekleniyor'}
                          </span>
                        </td>
                        <td className={classes.modalOrderActionsCell}>
                          <div className={classes.modalOrderActions}>
                            <button
                              type='button'
                              className={classes.modalIconAction}
                              onClick={() =>
                                setExpandedOrderKey(currentKey => (currentKey === orderKey ? null : orderKey))
                              }
                              disabled={busy}
                              aria-label={`${order.orderNumber} sipariş içeriğini ${
                                detailExpanded ? 'gizle' : 'göster'
                              }`}
                              aria-expanded={detailExpanded}
                              title={
                                detailExpanded
                                  ? 'Sipariş içeriğini gizle'
                                  : 'Sipariş içeriğini ve seri numaralarını göster'
                              }
                            >
                              <IconEye />
                            </button>
                            <button
                              type='button'
                              className={classes.modalRowAction}
                              onClick={() => trackOrder(order.orderNumber)}
                              disabled={busy || !canTrack}
                              title={canTrack ? 'Kargo durumunu sorgula' : 'Teslimat numarası henüz oluşmadı'}
                            >
                              Kargoyu sorgula
                            </button>
                          </div>
                        </td>
                      </tr>
                      <tr className={classes.modalOrderDetailRow} hidden={!detailExpanded}>
                        <td colSpan={4} className={classes.modalOrderDetailCell}>
                          <OrderDetailsPanel order={order} open={detailExpanded} disabled={busy} />
                        </td>
                      </tr>
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tracking && <CargoTrackingCard tracking={tracking} titleId='cargo-result-title' />}
    </FeatureModal>
  )
}
